import React, { useRef, useState } from "react";
import { View, FlatList, TouchableOpacity } from "react-native";
import { Text, RadioButton, Checkbox } from "react-native-paper";
import { Controller, useForm } from "react-hook-form";
import { Button, Modal, TextInput, ScrollableContainer, useErrorScroll, Dropdown } from "../../components";
import DatePickerInput from "../../components/DatePickerInput";
import { CreateHoleInOneInput } from "./types";
import moment from "moment";

// Expandable Section Component
interface ExpandableSectionProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}

const ExpandableSection: React.FC<ExpandableSectionProps> = ({ title, subtitle, children, defaultExpanded = false }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <View className="mb-6">
      <TouchableOpacity
        onPress={() => setIsExpanded(!isExpanded)}
        className="flex-row items-center justify-between p-4 bg-gray-50 rounded-lg mb-4"
      >
        <View className="flex-1">
          <Text className="text-regular-tight font-700 text-ink-darkest">{title}</Text>
          {subtitle && <Text className="text-small-normal text-ink-darker mt-1">{subtitle}</Text>}
        </View>
        <Text className="text-lg text-ink-darker ml-2">{isExpanded ? "−" : "+"}</Text>
      </TouchableOpacity>
      {isExpanded && <View>{children}</View>}
    </View>
  );
};

interface UserFacility {
  id: string;
  name: string;
}

interface FormData {
  golferName: string;
  golferHometown: string;
  golferEmail: string;
  golferPhone: string;
  occurredAt: string;
  holeNumber: string;
  yardage: string;
  clubUsed: string;
  clubBrand: string;
  clubModel: string;
  ballUsed: string;
  facilityId: string;
  description: string;
  age: string;
  gender: string;
  handicap: string;
  totalHios: string;
  yearsPlaying: string;
  witness1: string;
  witness2: string;
  witness3: string;
  displayOptIn: boolean;
  marketingOptIn: boolean;
  publicVisibility: boolean;
}

const defaultValues: FormData = {
  golferName: "",
  golferHometown: "",
  golferEmail: "",
  golferPhone: "",
  occurredAt: "",
  holeNumber: "",
  yardage: "",
  clubUsed: "",
  clubBrand: "",
  clubModel: "",
  ballUsed: "",
  facilityId: "",
  description: "",
  age: "",
  gender: "",
  handicap: "",
  totalHios: "",
  yearsPlaying: "",
  witness1: "",
  witness2: "",
  witness3: "",
  displayOptIn: false,
  marketingOptIn: false,
  publicVisibility: true,
};

interface Props {
  visible: boolean;
  onDismiss: () => void;
  onSubmit: (input: CreateHoleInOneInput) => Promise<void>;
  loading: boolean;
  userFacilities: UserFacility[];
}

export default function AddHoleInOneModal({ visible, onDismiss, onSubmit, loading, userFacilities }: Props) {
  const [errorMessage, setErrorMessage] = useState("");
  const flatListRef = useRef<FlatList>(null);

  // If there's only one facility, auto-select it
  const initialValues = {
    ...defaultValues,
    facilityId: userFacilities.length === 1 ? userFacilities[0].id : "",
  };

  const { control, handleSubmit, reset } = useForm<FormData>({
    defaultValues: initialValues,
  });

  const { scrollToField } = useErrorScroll({
    defaultValues: initialValues,
    flatListRef,
  });

  const validateDate = (date: moment.Moment) => {
    console.log("🔍 validateDate called with:", date?.format("YYYY-MM-DD"));
    const today = moment().startOf("day");
    const oneYearAgo = moment().subtract(1, "year").startOf("day");

    if (date.isAfter(today)) {
      return "Date cannot be in the future";
    }

    if (date.isBefore(oneYearAgo)) {
      return "Date cannot be more than 1 year ago";
    }
  };

  const validateHoleNumber = (value: string) => {
    console.log("🔍 validateHoleNumber called with:", value);
    if (!value) {
      return "Hole number is required";
    }
    const num = parseInt(value, 10);
    if (isNaN(num) || num < 1 || num > 18) {
      return "Hole number must be between 1 and 18";
    }
  };

  const validateOptionalNumber = (value: string, fieldName: string, min?: number, max?: number) => {
    if (!value) {
      return true; // Optional field
    }
    const num = parseFloat(value);
    if (isNaN(num)) {
      return `${fieldName} must be a valid number`;
    }
    if (min !== undefined && num < min) {
      return `${fieldName} must be at least ${min}`;
    }
    if (max !== undefined && num > max) {
      return `${fieldName} cannot exceed ${max}`;
    }
  };

  const onSubmitForm = async (data: FormData) => {
    try {
      setErrorMessage("");
      console.log("✅ Error message cleared");

      const witnesses = [data.witness1, data.witness2, data.witness3].filter((witness) => witness.trim().length > 0);

      const selectedFacility = userFacilities.find((f) => f.id === data.facilityId);

      const input: CreateHoleInOneInput = {
        golferName: data.golferName.trim(),
        golferHometown: data.golferHometown.trim(),
        golferEmail: data.golferEmail.trim() || undefined,
        golferPhone: data.golferPhone.trim() || undefined,
        occurredAt: data.occurredAt,
        locationId: data.facilityId,
        holeNumber: parseInt(data.holeNumber, 10),
        yardage: data.yardage ? parseInt(data.yardage, 10) : undefined,
        clubUsed: data.clubUsed.trim() || undefined,
        clubBrand: data.clubBrand.trim() || undefined,
        clubModel: data.clubModel.trim() || undefined,
        ballUsed: data.ballUsed.trim() || undefined,
        courseName: selectedFacility?.name || undefined,
        description: data.description.trim() || undefined,
        age: data.age ? parseInt(data.age, 10) : undefined,
        gender: data.gender.trim() || undefined,
        handicap: data.handicap ? parseFloat(data.handicap) : undefined,
        totalHios: data.totalHios ? parseInt(data.totalHios, 10) : undefined,
        yearsPlaying: data.yearsPlaying ? parseInt(data.yearsPlaying, 10) : undefined,
        witnesses: witnesses.length > 0 ? witnesses : undefined,
        displayOptIn: data.displayOptIn,
        marketingOptIn: data.marketingOptIn,
        publicVisibility: data.publicVisibility,
      };

      await onSubmit(input);
      reset();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Something went wrong";
      setErrorMessage(message);
      console.log("💬 Error message set to:", message);
    }
  };

  const handleDismiss = () => {
    reset();
    setErrorMessage("");
    onDismiss();
  };

  return (
    <Modal visible={visible} onDismiss={handleDismiss} title="Record Hole-in-One" fullScreen>
      <View style={{ flex: 1, flexDirection: "column" }}>
        <ScrollableContainer ref={flatListRef} style={{ flex: 1, overflow: "scroll" }} contentContainerClassName="pb-0">
          <View className="p-6 pb-0">
            <Text className="text-small-normal text-ink-darker mb-6">
              {userFacilities.length === 1
                ? `Recording hole-in-one for ${userFacilities[0].name}`
                : "Recording hole-in-one for your facility"}
            </Text>

            {/* Required Fields */}
            <View className="mb-8">
              <Text className="text-large-tight font-700 text-ink-darkest mb-6 border-b border-gray-200 pb-2">
                Required Information
              </Text>

              <Controller
                control={control}
                name="golferName"
                rules={{ required: "Golfer name is required" }}
                render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                  <TextInput
                    label="Golfer Name *"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    error={error}
                    onSubmitEditing={() => scrollToField("golferHometown")}
                    returnKeyType="next"
                    testID="golfer-name-input"
                  />
                )}
              />

              <View className="mt-4">
                <Controller
                  control={control}
                  name="golferHometown"
                  rules={{ required: "Golfer hometown is required" }}
                  render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                    <TextInput
                      label="Hometown *"
                      value={value}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      error={error}
                      onSubmitEditing={() => scrollToField("occurredAt")}
                      returnKeyType="next"
                      testID="golfer-hometown-input"
                    />
                  )}
                />
              </View>

              <View className="mt-4">
                <Controller
                  control={control}
                  name="occurredAt"
                  rules={{ required: "Date is required" }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <DatePickerInput
                      title="Date of Hole-in-One"
                      description="Select the date when the hole-in-one occurred"
                      placeholder="Select Date *"
                      value={value}
                      onChange={onChange}
                      validate={validateDate}
                      error={error}
                      onBlur={() => {}}
                      testID="occurred-at-input"
                    />
                  )}
                />
              </View>

              {userFacilities.length > 1 && (
                <View className="mt-4">
                  <Controller
                    control={control}
                    name="facilityId"
                    rules={{ required: "Facility is required" }}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Dropdown
                        label="Select Facility *"
                        setValue={onChange}
                        value={value}
                        list={userFacilities.map((facility) => ({
                          label: facility.name,
                          value: facility.id,
                        }))}
                        error={error}
                        testID="facility-select"
                      />
                    )}
                  />
                </View>
              )}

              <View className="mt-4">
                <Controller
                  control={control}
                  name="golferEmail"
                  render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                    <TextInput
                      label="Email"
                      value={value}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      error={error}
                      keyboardType="email-address"
                      autoCapitalize="none"
                      onSubmitEditing={() => scrollToField("holeNumber")}
                      returnKeyType="next"
                      testID="golfer-email-input"
                    />
                  )}
                />
              </View>

              <View className="mt-4">
                <Controller
                  control={control}
                  name="holeNumber"
                  rules={{ validate: validateHoleNumber }}
                  render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                    <TextInput
                      label="Hole Number *"
                      value={value}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      error={error}
                      keyboardType="number-pad"
                      placeholder="1-18"
                      onSubmitEditing={() => scrollToField("yardage")}
                      returnKeyType="next"
                      testID="hole-number-input"
                    />
                  )}
                />
              </View>
            </View>

            {/* Optional Sections */}
            <ExpandableSection title="Additional Golfer Details" subtitle="Age, contact info, playing background">
              <Controller
                control={control}
                name="age"
                rules={{ validate: (value) => validateOptionalNumber(value, "Age", 5, 120) }}
                render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                  <TextInput
                    label="Age"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    error={error}
                    keyboardType="number-pad"
                    onSubmitEditing={() => scrollToField("gender")}
                    returnKeyType="next"
                    testID="age-input"
                  />
                )}
              />

              <View className="mt-4">
                <Controller
                  control={control}
                  name="gender"
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Dropdown
                      label="Gender"
                      setValue={onChange}
                      value={value ?? ""}
                      list={[
                        { label: "Male", value: "Male" },
                        { label: "Female", value: "Female" },
                        { label: "Not Specified", value: "Not Specified" },
                      ]}
                      error={error}
                      testID="gender-select"
                    />
                  )}
                />
              </View>

              <View className="mt-4">
                <Controller
                  control={control}
                  name="golferPhone"
                  render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                    <TextInput
                      label="Phone"
                      value={value}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      error={error}
                      keyboardType="phone-pad"
                      onSubmitEditing={() => scrollToField("handicap")}
                      returnKeyType="next"
                      testID="golfer-phone-input"
                    />
                  )}
                />
              </View>

              <View className="mt-4">
                <Controller
                  control={control}
                  name="handicap"
                  rules={{ validate: (value) => validateOptionalNumber(value, "Handicap", -10, 54) }}
                  render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                    <TextInput
                      label="Handicap"
                      value={value}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      error={error}
                      keyboardType="numeric"
                      onSubmitEditing={() => scrollToField("totalHios")}
                      returnKeyType="next"
                      testID="handicap-input"
                    />
                  )}
                />
              </View>

              <View className="mt-4">
                <Controller
                  control={control}
                  name="totalHios"
                  rules={{ validate: (value) => validateOptionalNumber(value, "Total Hole-in-Ones", 0, 100) }}
                  render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                    <TextInput
                      label="Lifetime HIOs made by this golfer"
                      value={value}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      error={error}
                      keyboardType="number-pad"
                      onSubmitEditing={() => scrollToField("yearsPlaying")}
                      returnKeyType="next"
                      testID="total-hios-input"
                    />
                  )}
                />
              </View>

              <View className="mt-4">
                <Controller
                  control={control}
                  name="yearsPlaying"
                  rules={{ validate: (value) => validateOptionalNumber(value, "Years Playing", 1, 80) }}
                  render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                    <TextInput
                      label="Years Playing Golf"
                      value={value}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      error={error}
                      keyboardType="number-pad"
                      returnKeyType="next"
                      testID="years-playing-input"
                    />
                  )}
                />
              </View>
            </ExpandableSection>

            <ExpandableSection title="Equipment Details" subtitle="Yardage, club and ball information">
              <Controller
                control={control}
                name="yardage"
                rules={{ validate: (value) => validateOptionalNumber(value, "Yardage", 1, 1000) }}
                render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                  <TextInput
                    label="Yardage"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    error={error}
                    keyboardType="number-pad"
                    placeholder="Distance in yards"
                    onSubmitEditing={() => scrollToField("clubUsed")}
                    returnKeyType="next"
                    testID="yardage-input"
                  />
                )}
              />

              <View className="mt-4">
                <Controller
                  control={control}
                  name="clubUsed"
                  render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                    <TextInput
                      label="Club Used"
                      value={value}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      error={error}
                      placeholder="e.g., 7 Iron, Driver"
                      onSubmitEditing={() => scrollToField("clubBrand")}
                      returnKeyType="next"
                      testID="club-used-input"
                    />
                  )}
                />
              </View>

              <View className="mt-4">
                <Controller
                  control={control}
                  name="clubBrand"
                  render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                    <TextInput
                      label="Club Brand"
                      value={value}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      error={error}
                      placeholder="e.g., Callaway, TaylorMade"
                      onSubmitEditing={() => scrollToField("clubModel")}
                      returnKeyType="next"
                      testID="club-brand-input"
                    />
                  )}
                />
              </View>

              <View className="mt-4">
                <Controller
                  control={control}
                  name="clubModel"
                  render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                    <TextInput
                      label="Club Model"
                      value={value}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      error={error}
                      placeholder="e.g., Big Bertha, SIM2"
                      onSubmitEditing={() => scrollToField("ballUsed")}
                      returnKeyType="next"
                      testID="club-model-input"
                    />
                  )}
                />
              </View>

              <View className="mt-4">
                <Controller
                  control={control}
                  name="ballUsed"
                  render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                    <TextInput
                      label="Ball Used"
                      value={value}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      error={error}
                      placeholder="e.g., Titleist Pro V1"
                      returnKeyType="next"
                      testID="ball-used-input"
                    />
                  )}
                />
              </View>
            </ExpandableSection>

            <ExpandableSection title="Witnesses" subtitle="Up to 3 witnesses can be recorded">
              <Controller
                control={control}
                name="witness1"
                render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                  <TextInput
                    label="Witness 1"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    error={error}
                    onSubmitEditing={() => scrollToField("witness2")}
                    returnKeyType="next"
                    testID="witness1-input"
                  />
                )}
              />

              <View className="mt-4">
                <Controller
                  control={control}
                  name="witness2"
                  render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                    <TextInput
                      label="Witness 2"
                      value={value}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      error={error}
                      onSubmitEditing={() => scrollToField("witness3")}
                      returnKeyType="next"
                      testID="witness2-input"
                    />
                  )}
                />
              </View>

              <View className="mt-4">
                <Controller
                  control={control}
                  name="witness3"
                  render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                    <TextInput
                      label="Witness 3"
                      value={value}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      error={error}
                      returnKeyType="next"
                      testID="witness3-input"
                    />
                  )}
                />
              </View>
            </ExpandableSection>

            <ExpandableSection title="Additional Details" subtitle="Description and other information">
              <Controller
                control={control}
                name="description"
                render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                  <TextInput
                    label="Description"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    error={error}
                    multiline
                    numberOfLines={3}
                    placeholder="Additional details about the shot"
                    testID="description-input"
                  />
                )}
              />
            </ExpandableSection>

            <ExpandableSection
              title="Privacy & Marketing Options"
              subtitle="Control visibility and communications"
              defaultExpanded={true}
            >
              <View className="mb-4">
                <Text className="text-regular-tight font-600 text-ink-darkest mb-3">Visibility</Text>
                <Controller
                  control={control}
                  name="publicVisibility"
                  render={({ field: { onChange, value } }) => (
                    <View>
                      <View className="flex-row items-center mb-2">
                        <RadioButton value="true" status={value ? "checked" : "unchecked"} onPress={() => onChange(true)} />
                        <Text className="ml-2 flex-1 text-small-normal text-ink-darkest">
                          Make this hole-in-one publicly visible
                        </Text>
                      </View>
                      <View className="flex-row items-center">
                        <RadioButton value="false" status={!value ? "checked" : "unchecked"} onPress={() => onChange(false)} />
                        <Text className="ml-2 flex-1 text-small-normal text-ink-darkest">Keep this hole-in-one private</Text>
                      </View>
                    </View>
                  )}
                />
              </View>

              <View className="border-t border-gray-200 pt-4">
                <Text className="text-regular-tight font-600 text-ink-darkest mb-3">Marketing Preferences</Text>

                <Controller
                  control={control}
                  name="displayOptIn"
                  render={({ field: { onChange, value } }) => (
                    <View className="flex-row items-center mb-4">
                      <Checkbox status={value ? "checked" : "unchecked"} onPress={() => onChange(!value)} />
                      <Text className="ml-2 flex-1 text-small-normal text-ink-darkest">
                        Allow PGA to display this achievement in promotional materials
                      </Text>
                    </View>
                  )}
                />

                <Controller
                  control={control}
                  name="marketingOptIn"
                  render={({ field: { onChange, value } }) => (
                    <View className="flex-row items-center">
                      <Checkbox status={value ? "checked" : "unchecked"} onPress={() => onChange(!value)} />
                      <Text className="ml-2 flex-1 text-small-normal text-ink-darkest">
                        Send marketing communications about this achievement
                      </Text>
                    </View>
                  )}
                />
              </View>
            </ExpandableSection>

            {/* Buttons at the bottom of the form */}
            <View className="mt-8 pt-6 border-t border-gray-200">
              {errorMessage ? (
                <View className="mb-4">
                  <Text className="text-red-600 text-sm">{errorMessage}</Text>
                </View>
              ) : null}
              <View className="flex-row justify-between mb-4">
                <Button onPress={handleDismiss} disabled={loading}>
                  Cancel
                </Button>
                <Button
                  mode="contained"
                  onPress={() => {
                    handleSubmit(onSubmitForm)();
                  }}
                  loading={loading}
                  disabled={loading}
                  testID="submit-hole-in-one-button"
                >
                  Submit
                </Button>
              </View>
            </View>
          </View>
        </ScrollableContainer>
      </View>
    </Modal>
  );
}
